import React, { useState } from 'react'
import { connectToggleRefinement } from 'react-instantsearch-dom'
import PropTypes from 'prop-types'
import { Accordion, Level, Icon, Box, Text, List } from '@deseretbook/react-ui'
import { Analytics } from '../../../../dbcom'
import { ClickableContent } from '../..'
import { trackAlgoliaEvent } from '../analytics'

/**
 * Component for toggling boolean refinements (isPlus, onSale)
 *
 * This is a dynamic component that can work with any boolean related values
 *
 * @see https://www.algolia.com/doc/api-reference/widgets/toggle-refinement/react/
 *
 * @param {boolean} currentRefinement - whether the toggle is true/false
 * @param {string} title - Title for toggle section
 * @param {string} label - Label used for the refinement
 * @param {object} count - Counts for checked/unchecked settings
 * @param {function} refine - Updates the refinement
 * @param {function} children - React children, normally used for label
 * @param {boolean} isOpenByDefault - Whether the filter should be open on load
 * @param {string} attribute - The toggle attribute name
 *
 * @returns {function} React Component
 */
const SubscribableItemContainer = ({
  currentRefinement,
  label,
  count,
  refine,
  children,
  attribute,
}) => {
  /**
   * Handles/Tracks user applied filter
   *
   */
  const handleClick = () => {
    if (!count.unchecked) {
      return
    }

    refine(!currentRefinement)

    // Track event on GTM
    Analytics.event(
      {
        category: 'Algolia Filter Item',
        action: `User applied filter, ${label}.`,
      },
      {
        isInteractive: true,
      },
    )

    // Track event with Algolia
    trackAlgoliaEvent('clickedFilters', {
      eventName: 'Filter Clicked',
      filters: [`${attribute}:${!currentRefinement}`],
    })
  }

  return (
    <List.Item>
      <ClickableContent isPaddingless onClick={handleClick} isDisabled={!count.unchecked}>
        <Level>
          <Level.Left style={{ maxWidth: '80%' }}>
            <Level.Item>
              <Icon name={`checkbox${currentRefinement ? 'On' : 'Off'}`} />
            </Level.Item>
            <Level.Item>{children}</Level.Item>
          </Level.Left>
          <Level.Right>
            <Level.Item>
              <Text>({count.unchecked || 0})</Text>
            </Level.Item>
          </Level.Right>
        </Level>
      </ClickableContent>
    </List.Item>
  )
}

SubscribableItemContainer.propTypes = {
  currentRefinement: PropTypes.bool,
  count: PropTypes.shape({
    checked: PropTypes.number,
    unchecked: PropTypes.number,
  }),
  label: PropTypes.string,
  refine: PropTypes.func,
  children: PropTypes.node,
  attribute: PropTypes.string,
}

/**
 * Refinement List component for showing subscribable products using Logos
 *
 * @param {string} label - Label for the accordion.
 * @param {Array} children - Array of JSX elements
 * @param {boolean} isOpenByDefault - Boolean value for opening or closing the accordian by default
 *
 * @returns {function} React Component
 */
const SubscribableListContainer = ({ label, children, isOpenByDefault = false, value }) => {
  const [isOpen, setIsOpen] = useState(isOpenByDefault)

  return (
    <Accordion.Section isOpen={isOpen} onToggle={() => setIsOpen(!isOpen)}>
      <Accordion.Section.Header>
        <Box paddingVertical={0.5}>
          <Level>
            <Level.Left>
              <Level.Item>
                <Text style={{ textTransform: 'uppercase' }}>{label}</Text>
              </Level.Item>
            </Level.Left>
            <Level.Right>
              <Level.Item>
                <Icon name={`chevron${isOpen ? 'Up' : 'Down'}`} />
              </Level.Item>
            </Level.Right>
          </Level>
        </Box>
      </Accordion.Section.Header>
      <Accordion.Section.Content>
        <Box paddingVertical={1} paddingLeft={0.5}>
          <List>
            {React.Children.map(children, (child, index) => (
              <SubscribableToggleComponent
                attribute={child.props.attribute}
                key={`${label} : ${child.props.attribute}`}
                index={index}
                value
              >
                {React.cloneElement(child)}
              </SubscribableToggleComponent>
            ))}
          </List>
        </Box>
      </Accordion.Section.Content>
    </Accordion.Section>
  )
}

SubscribableListContainer.propTypes = {
  label: PropTypes.string,
  isOpenByDefault: PropTypes.bool,
  children: PropTypes.node,
  value: PropTypes.bool,
}

const SubscribableToggleComponent = connectToggleRefinement(SubscribableItemContainer)

export const Subscribable = SubscribableListContainer
